import React, { createContext, useContext } from 'react';

/**
 * Whether a field error in this subtree announces itself when it appears.
 *
 * DEFAULT IS `true`, and that is deliberate: most places a donor field error
 * appears, it appears one at a time and in response to something the donor just
 * did, where an unsolicited announcement is exactly right. Only a subtree that
 * can produce N errors at once opts out, and it does so by wrapping itself —
 * never by every call site remembering a prop.
 *
 * decisions.md D-con-120-single-announcement.
 */
const FieldErrorAnnouncementContext = createContext(true);

/**
 * Turns off the unsolicited announcement for every field error inside it.
 *
 * WHAT THIS DOES NOT DO, and the distinction is the whole decision: the error
 * keeps its visible text and keeps being reachable as its field's description
 * through `aria-describedby`. A screen-reader donor who navigates TO the field
 * still hears the error. What stops is the error announcing itself the moment
 * it renders — which, on a blocked submit with three invalid address fields, is
 * three polite regions firing at once and one of them winning.
 *
 * The single announcement in that subtree is instead the FOCUS MOVE the blocked
 * submit already performs (`focusFirstInvalidField`): the donor is taken to the
 * first blocking field and hears that field, its label and its error, once.
 */
export function QuietFieldErrors({ children }) {
  return (
    <FieldErrorAnnouncementContext.Provider value={false}>
      {children}
    </FieldErrorAnnouncementContext.Provider>
  );
}

// The one donor field error (CON-11). Consolidated from three copies: this one,
// donation-elements' byte-identical twin, and the checkout's pre-refactor
// version, which drove the same fade from component state and a setTimeout.
//
// Fade-in is driven purely by CSS on mount. The component only renders when
// `error` is truthy, so mounting === error appearing → one clean fadeSlideIn.
// `key={error}` remounts (and re-runs the animation) only when the message text
// actually changes. No effect/state churn → no double render, no double animation.
const AnimatedError = ({ error, className = "", id }) => {
  const announces = useContext(FieldErrorAnnouncementContext);
  if (!error) return null;

  // Inside a QuietFieldErrors subtree both attributes come off together. Keeping
  // `role="alert"` while dropping `aria-live` would not go quiet — the role
  // carries an implicit assertive live region of its own, which is louder than
  // the polite one it replaced.
  const liveRegionProps = announces ? { role: 'alert', 'aria-live': 'polite' } : {};

  // Donor redesign (SPEC §C Inputs, CON-515): 13/18 message beside a 14 error
  // icon in the same ink. The icon is decorative (aria-hidden), so the live
  // region's text — and every test that reads it — is the message alone.
  return (
    <div
      key={error}
      id={id}
      className={`animate-fade-in mt-1 flex items-start gap-[var(--cs-space-1)] font-sans text-[length:var(--cs-text-xs-plus)] leading-[var(--cs-leading-18)] font-medium text-[color:var(--cs-error-color)] ${className}`}
      {...liveRegionProps}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        className="mt-[var(--cs-space-hairline)] h-[var(--cs-icon-sm)] w-[var(--cs-icon-sm)] shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <span>{error}</span>
    </div>
  );
};

export default AnimatedError;
